import type { ReactNode } from 'react'

import { Link } from '@/lib/router'

import { Kpi } from '@/components/ui/kpi'
import { inr, num } from '@/lib/format'

import type { CollectionSplit } from './api'

/**
 * Paid / Unpaid cases over every open case the caller can see. No Total tile:
 * it always equals the "Open cases" KPI above it.
 *
 * <p>The numbers are the server's `collection` block from `/v1/cases/summary`
 * — the one the agent app's "My cases" card reads — so web and mobile agree
 * on what "paid" means: anything collected since the dues file (a pending
 * cash receipt or a successful digital payment), partial or full. Counted in
 * SQL over the whole scoped book, so Paid + Unpaid always equals Open cases.</p>
 *
 * <p>Paid and Unpaid drill into the queue filtered by the same rule
 * (`collection=PAID|UNPAID`), so each tile opens exactly the cases it counts.</p>
 */
export function CollectionSplitStrip({
  split,
  loading,
}: {
  split: CollectionSplit | null
  loading: boolean
}) {
  // An API without the split sends no block; show nothing rather than zeros
  // that read as "nobody has paid".
  if (!loading && !split) return null

  const share = (count: number) =>
    split && split.totalCount > 0 ? `${Math.round((count / split.totalCount) * 100)}% of cases · ` : ''

  return (
    <section aria-label="Paid and unpaid cases" className="space-y-2">
      <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
        Paid vs unpaid · this cycle
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Drill search={{ collection: 'PAID' }}>
          <Kpi
            label="Paid cases"
            value={split ? num(split.paidCount) : '—'}
            hint={split ? `${share(split.paidCount)}${inr(split.paidAmount)} collected` : undefined}
            loading={loading}
            deltaSense="higher-is-better"
          />
        </Drill>
        <Drill search={{ collection: 'UNPAID' }}>
          <Kpi
            label="Unpaid cases"
            value={split ? num(split.unpaidCount) : '—'}
            hint={split ? `${share(split.unpaidCount)}${inr(split.unpaidDue)} still due` : undefined}
            loading={loading}
            deltaSense="lower-is-better"
          />
        </Drill>
      </div>
    </section>
  )
}

function Drill({ search, children }: { search: Record<string, string>; children: ReactNode }) {
  return (
    <Link
      to="/cases"
      // /cases reads its filters loosely from the URL; see DashboardPage's DrillKpi.
      search={search as never}
      className="block rounded-md outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40"
    >
      {children}
    </Link>
  )
}
